/**
 * Deterministic sampling of a model's variables.
 *
 * `sampleInto(env, iteration)` is a pure function of (seed, iteration): any
 * worker can produce the sample vector for any iteration index without
 * coordination, which is what makes results independent of worker count
 * (PRD §6.2).
 *
 * Correlation uses a Gaussian copula with equicorrelation within each group.
 * Each member's underlying normal is
 *
 *     z_i = sqrt(rho) * z_group + sqrt(1 - rho) * z_i_independent
 *
 * giving pairwise correlation exactly rho between members while leaving each
 * marginal distribution untouched, since the copula's uniform is fed back
 * through the marginal's own inverse CDF. rho = 1 makes a perfectly matched
 * set — the case that must drive a matched-pair divider's output variance to
 * zero (PRD §5).
 *
 * The hot loop is written against flat typed arrays rather than the
 * `Distribution` objects. Reading `d.kind` and switching on a string per
 * variable per iteration costs more than the arithmetic it dispatches to, so
 * parameters are unpacked once at construction into numeric form.
 */

import { CounterRng, GROUP_SLOT_BASE } from './prng.ts';
import { type Distribution, quantile } from './distributions.ts';
import { normalCdf, normalInv } from './special.ts';
import type { CompiledModel } from './model.ts';

// Numeric kind codes, so the inner switch compiles to a jump table.
const K_FIXED = 0;
const K_UNIFORM = 1;
const K_NORMAL = 2;
const K_LOGNORMAL = 3;
const K_TRIANGULAR = 4;
/** Anything not worth a fast path falls back to the generic inverse CDF. */
const K_GENERIC = 5;

export class Sampler {
  private readonly rng: CounterRng;
  private readonly n: number;

  private readonly kinds: Int8Array;
  private readonly p0: Float64Array;
  private readonly p1: Float64Array;
  private readonly p2: Float64Array;
  private readonly p3: Float64Array;
  private readonly generic: (Distribution | null)[];

  private readonly varGroup: Int32Array;
  private readonly groupZ: Float64Array;
  private readonly sqrtRho: Float64Array;
  private readonly sqrtOneMinusRho: Float64Array;
  private readonly anyGroups: boolean;

  // Explicit correlation matrix: independent normals w, correlated z = L*w.
  private readonly varCorrRow: Int32Array;
  private readonly corrRowVar: Int32Array;
  private readonly chol: Float64Array | null;
  private readonly corrK: number;
  private readonly w: Float64Array;

  private readonly pair = new Float64Array(2);

  constructor(model: CompiledModel, seed: number) {
    this.rng = new CounterRng(seed);
    this.n = model.varIds.length;
    this.varGroup = model.varGroup;
    this.anyGroups = model.groups.length > 0;

    this.kinds = new Int8Array(this.n);
    this.p0 = new Float64Array(this.n);
    this.p1 = new Float64Array(this.n);
    this.p2 = new Float64Array(this.n);
    this.p3 = new Float64Array(this.n);
    this.generic = new Array(this.n).fill(null);

    model.distributions.forEach((d, i) => {
      switch (d.kind) {
        case 'fixed':
          this.kinds[i] = K_FIXED;
          this.p0[i] = d.value;
          break;
        case 'uniform':
          this.kinds[i] = K_UNIFORM;
          this.p0[i] = d.min;
          this.p1[i] = d.max - d.min;
          break;
        case 'normal':
          this.kinds[i] = K_NORMAL;
          this.p0[i] = d.mu;
          this.p1[i] = d.sigma;
          break;
        case 'lognormal':
          this.kinds[i] = K_LOGNORMAL;
          this.p0[i] = d.muLog;
          this.p1[i] = d.sigmaLog;
          break;
        case 'triangular':
          this.kinds[i] = K_TRIANGULAR;
          this.p0[i] = d.min;
          this.p1[i] = d.max;
          this.p2[i] = d.mode;
          this.p3[i] = d.max === d.min ? 0 : (d.mode - d.min) / (d.max - d.min);
          break;
        default:
          // Truncated normal and discrete keep the generic path: they are less
          // common and their setup does not reduce to four scalars.
          this.kinds[i] = K_GENERIC;
          this.generic[i] = d;
      }
    });

    this.groupZ = new Float64Array(model.groups.length);
    this.sqrtRho = new Float64Array(model.groups.length);
    this.sqrtOneMinusRho = new Float64Array(model.groups.length);
    model.groups.forEach((g, i) => {
      this.sqrtRho[i] = Math.sqrt(g.rho);
      this.sqrtOneMinusRho[i] = Math.sqrt(1 - g.rho);
    });

    this.varCorrRow = model.varCorrRow;
    this.corrRowVar = model.corrRowVar;
    this.chol = model.correlation?.cholesky ?? null;
    this.corrK = model.correlation?.ids.length ?? 0;
    this.w = new Float64Array(this.corrK);
  }

  /** Map a uniform to variable `i`'s marginal. */
  private draw(i: number, u: number): number {
    switch (this.kinds[i]) {
      case K_FIXED:
        return this.p0[i];
      case K_UNIFORM:
        return this.p0[i] + u * this.p1[i];
      case K_NORMAL:
        return this.p0[i] + this.p1[i] * normalInv(u);
      case K_LOGNORMAL:
        return Math.exp(this.p0[i] + this.p1[i] * normalInv(u));
      case K_TRIANGULAR: {
        const min = this.p0[i];
        const max = this.p1[i];
        const mode = this.p2[i];
        const span = max - min;
        if (span === 0) return min;
        return u < this.p3[i]
          ? min + Math.sqrt(u * span * (mode - min))
          : max - Math.sqrt((1 - u) * span * (max - mode));
      }
      default:
        return quantile(this.generic[i]!, u);
    }
  }

  /**
   * Fill the variable slots of `env` for the given iteration.
   *
   * Intermediate slots are left untouched; call `model.fillIntermediates`
   * afterwards (the Monte Carlo loop does both in sequence).
   */
  sampleInto(env: Float64Array, iteration: number): void {
    const n = this.n;
    const pair = this.pair;

    // One shared draw per group, before any member is sampled. This is the
    // mechanical expression of "all parts in a lot see the same common-mode
    // shift" and of "all components see the same temperature".
    if (this.anyGroups) {
      for (let g = 0; g < this.groupZ.length; g++) {
        this.groupZ[g] = normalInv(this.rng.uniform(iteration, GROUP_SLOT_BASE + g));
      }
    }

    for (let i = 0; i < n; i += 2) {
      // One Philox invocation serves two variables.
      this.rng.uniformPair(iteration, i >>> 1, pair);
      this.place(env, i, pair[0]);
      if (i + 1 < n) this.place(env, i + 1, pair[1]);
    }

    // Matrix-correlated variables were deferred: their values depend on every
    // member's independent normal, so none can be resolved until all are drawn.
    if (this.chol !== null) {
      const k = this.corrK;
      const l = this.chol;
      for (let row = 0; row < k; row++) {
        let z = 0;
        // Lower triangular, so row `row` mixes only the first `row + 1` normals.
        for (let m = 0; m <= row; m++) z += l[row * k + m] * this.w[m];
        const vi = this.corrRowVar[row];
        env[vi] = this.draw(vi, clampUnit(normalCdf(z)));
      }
    }
  }

  /** Resolve one variable, or stash its normal when the matrix must resolve it. */
  private place(env: Float64Array, i: number, u: number): void {
    const row = this.varCorrRow[i];
    if (row >= 0) {
      this.w[row] = normalInv(u);
      return;
    }
    const g = this.varGroup[i];
    env[i] = g < 0 ? this.draw(i, u) : this.drawCorrelated(i, g, u);
  }

  /**
   * Ungrouped variables use their uniform directly; skipping the
   * normalInv/normalCdf round trip avoids both its cost and its rounding error.
   * Grouped ones must pass through normal space to pick up the common-mode draw.
   */
  private drawCorrelated(i: number, g: number, u: number): number {
    const z = this.sqrtRho[g] * this.groupZ[g] + this.sqrtOneMinusRho[g] * normalInv(u);
    return this.draw(i, clampUnit(normalCdf(z)));
  }
}

/**
 * Keep copula probabilities strictly inside (0,1).
 *
 * normalCdf saturates to exactly 0 or 1 beyond ~+/-37 sigma, and feeding that
 * to an inverse CDF yields +/-Infinity. Nudging to the nearest representable
 * interior value costs nothing and keeps a 10^7-sample run from producing a
 * single poisoned NaN.
 */
function clampUnit(u: number): number {
  if (u <= 0) return Number.MIN_VALUE;
  if (u >= 1) return 1 - Number.EPSILON / 2;
  return u;
}
